import { useState } from "react";

import type { Column } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useMe } from "@/hooks/use-auth";
import { useReceivables } from "@/hooks/use-receivables";
import { useServerTable } from "@/hooks/use-server-table";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { formatCurrency } from "@/lib/utils";
import { receivableService } from "@/services/receivable";

import { RECEIVABLE_STATUS_LABELS } from "./receivable-labels";
import { ReceivablePaymentDialog } from "./receivable-payment-dialog";

import type { Receivable, ReceivableStatus } from "@artisancode/api-types";

export function ReceivableList() {
  const [openSaleId, setOpenSaleId] = useState<string | null>(null);

  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<Receivable>({
    queryKey: (params) => queryKeys.receivables.list(params),
    fetcher: (params) => receivableService.list(params),
    pageSize: 10,
    filterKeys: ["status", "date_from", "date_to", "workspace_id"],
  });

  // Keep the dialog's receivable prop live from the current query result so
  // recording/verifying/deleting a payment while the dialog is open reflects
  // immediately (both table list state and loadedItems cover desktop/mobile).
  const { data: liveData } = useReceivables();
  const editing =
    liveData?.items.find((r) => r.saleId === openSaleId) ??
    table.items.find((r) => r.saleId === openSaleId) ??
    null;

  const columns: Column<Receivable>[] = [
    {
      key: "date",
      label: "Tanggal",
      render: (r) => (
        <div className="flex flex-col">
          <span>{r.date}</span>
          <span className="text-xs text-muted-foreground">
            Jatuh tempo {r.dueDate}
          </span>
        </div>
      ),
    },
    {
      key: "customer",
      label: "Pelanggan / Produk",
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.customerName || "-"}</span>
          <span className="text-xs text-muted-foreground">
            {r.items[0]?.productName ?? "-"}
            {r.items.length > 1 ? ` +${r.items.length - 1} lainnya` : ""}
          </span>
        </div>
      ),
    },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (r: Receivable) => (
              <span className="text-sm">{workspaceName(r.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "amount",
      label: "Sisa / Total",
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {formatCurrency(r.remainingAmount)}
          </span>
          <span className="text-xs text-muted-foreground">
            dari {formatCurrency(r.totalAmount)}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <div className="flex flex-col gap-1">
          <Badge
            variant={
              r.status === "settled"
                ? "default"
                : r.status === "overdue"
                  ? "destructive"
                  : "outline"
            }
          >
            {RECEIVABLE_STATUS_LABELS[r.status]}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {r.ageDays} hari
          </span>
        </div>
      ),
    },
    {
      key: "pic",
      label: "PIC Penagihan",
      render: (r) => <span className="text-sm">{r.collectorName || "-"}</span>,
    },
  ];

  const statusOptions: { label: string; value: ReceivableStatus }[] = [
    { label: "Belum Lunas", value: "open" },
    { label: "Jatuh Tempo", value: "overdue" },
    { label: "Lunas", value: "settled" },
  ];

  return (
    <div>
      <PageHeader
        title="Piutang"
        description="Pantau sisa tagihan, riwayat pembayaran, dan penagihan per transaksi."
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        searchPlaceholder="Cari pelanggan / ID transaksi..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        filters={[
          ...workspaceFilter,
          { key: "status", label: "Status", options: statusOptions },
        ]}
        dateRange={{
          from: table.activeFilters.date_from ?? "",
          to: table.activeFilters.date_to ?? "",
          onChange: (date_from, date_to) =>
            table.onFiltersChange({ date_from, date_to }),
        }}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        loading={table.loading}
        actions={(item) => (
          <button
            type="button"
            className="text-sm text-primary underline-offset-2 hover:underline"
            onClick={() => setOpenSaleId(item.saleId)}
          >
            Detail
          </button>
        )}
        mobileRender={(r) => (
          <Card className="overflow-hidden border border-border shadow-xs">
            <div className="p-3.5 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-sm">{r.date}</span>
                  <span className="text-xs text-muted-foreground">
                    (Tempo: {r.dueDate})
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant={
                      r.status === "settled"
                        ? "default"
                        : r.status === "overdue"
                          ? "destructive"
                          : "outline"
                    }
                    className="text-[11px] px-2 py-0.5"
                  >
                    {RECEIVABLE_STATUS_LABELS[r.status]}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground font-medium">
                    {r.ageDays} hr
                  </span>
                </div>
              </div>

              <div>
                <p className="font-semibold text-sm text-foreground">
                  {r.customerName || "Pelanggan Umum"}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {r.items[0]?.productName ?? "-"}
                  {r.items.length > 1 ? ` +${r.items.length - 1} lainnya` : ""}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-2.5 text-xs">
                <div>
                  <span className="text-[11px] text-muted-foreground block">
                    Sisa Tagihan
                  </span>
                  <span className="font-bold text-sm text-destructive">
                    {formatCurrency(r.remainingAmount)}
                  </span>
                  <span className="text-[11px] text-muted-foreground block">
                    Total: {formatCurrency(r.totalAmount)}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[11px] text-muted-foreground block">
                    PIC Penagihan
                  </span>
                  <span className="font-medium text-foreground text-xs block truncate">
                    {r.collectorName || "-"}
                  </span>
                </div>
              </div>

              <div className="flex justify-end pt-1 border-t border-border/40">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs font-medium text-primary hover:bg-primary/10"
                  onClick={() => setOpenSaleId(r.saleId)}
                >
                  Detail & Riwayat Pembayaran →
                </Button>
              </div>
            </div>
          </Card>
        )}
      />

      <ReceivablePaymentDialog
        open={openSaleId !== null}
        onOpenChange={(next) => !next && setOpenSaleId(null)}
        receivable={editing}
      />
    </div>
  );
}
